import React, { useEffect, useState } from 'react';
import { CreditCard, Loader2, LogOut, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api } from '../../lib/api';
import {
  type BillingState,
  cycleLabel,
  isBillingOperational,
  statusLabel,
  subscriptionLabel,
} from '../../lib/billing';
import { formatPrice } from '../../lib/mockData';
import { useAuthStore } from '../../store/authStore';

const AdminBillingRequired: React.FC = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [billing, setBilling] = useState<BillingState | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirecting, setRedirecting] = useState(false);
  const [feedback, setFeedback] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.establishmentId) {
      setLoading(false);
      return;
    }

    api.get(`/billing/establishment/${user.establishmentId}`)
      .then(({ data }) => {
        if (isBillingOperational(data)) {
          navigate('/admin', { replace: true });
          return;
        }
        setBilling(data);
      })
      .catch((error) => {
        console.error('Failed to load billing state', error);
        setFeedback('Nao foi possivel carregar os dados da assinatura.');
      })
      .finally(() => setLoading(false));
  }, [navigate, user?.establishmentId]);

  useEffect(() => {
    const billingResult = searchParams.get('billing');
    if (billingResult === 'success') {
      setFeedback('Pagamento iniciado. Assim que a Stripe confirmar, o painel sera liberado automaticamente.');
    }
    if (billingResult === 'cancel') {
      setFeedback('Pagamento cancelado. Para liberar o painel, finalize a assinatura.');
    }
  }, [searchParams]);

  const openCheckout = async () => {
    if (!user?.establishmentId) return;
    try {
      setRedirecting(true);
      const { data } = await api.post('/billing/checkout-session', { establishmentId: user.establishmentId });
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to create checkout session', error);
      setFeedback('Nao foi possivel abrir o pagamento. Verifique a configuracao da Stripe.');
      setRedirecting(false);
    }
  };

  const openPortal = async () => {
    if (!user?.establishmentId) return;
    try {
      setRedirecting(true);
      const { data } = await api.post('/billing/portal-session', { establishmentId: user.establishmentId });
      window.location.href = data.url;
    } catch (error) {
      console.error('Failed to create portal session', error);
      setFeedback('Nao foi possivel abrir o portal do cliente.');
      setRedirecting(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/admin/login';
  };

  return (
    <main className="min-h-screen bg-background px-4 py-8 text-white">
      <section className="mx-auto flex min-h-[calc(100vh-4rem)] max-w-3xl flex-col justify-center">
        <div className="rounded-3xl border border-[#2A2A2A] bg-surface p-6 shadow-2xl md:p-8">
          <div className="mb-8 flex items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/15 text-primary">
                <CreditCard className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary">Pagamento obrigatorio</p>
                <h1 className="mt-2 text-2xl font-black tracking-tight md:text-3xl">Assinatura vencida</h1>
              </div>
            </div>
            <button
              type="button"
              onClick={handleLogout}
              className="rounded-xl border border-[#2A2A2A] p-2 text-muted-foreground transition hover:text-white"
              title="Sair"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>

          <p className="max-w-2xl text-sm leading-6 text-muted-foreground">
            O trial ou a assinatura deste restaurante venceu. Para continuar usando o painel do Garcom Digital,
            finalize o pagamento da assinatura. Enquanto isso, o acesso fica limitado a esta tela.
          </p>

          {feedback && (
            <div className="mt-5 rounded-2xl border border-primary/30 bg-primary/10 px-4 py-3 text-sm text-primary">
              {feedback}
            </div>
          )}

          {loading ? (
            <div className="mt-8 flex items-center gap-3 rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando assinatura...
            </div>
          ) : (
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Restaurante</p>
                <p className="mt-2 text-lg font-bold">{billing?.name ?? user?.name ?? 'Restaurante'}</p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Status</p>
                <p className="mt-2 text-lg font-bold text-primary">
                  {billing ? statusLabel[billing.subscriptionStatus] ?? billing.subscriptionStatus : 'Indisponivel'}
                </p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Plano</p>
                <p className="mt-2 text-lg font-bold">
                  {billing ? subscriptionLabel[billing.subscriptionPlan] ?? billing.subscriptionPlan : 'Basic'}
                </p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Ciclo e valor</p>
                <p className="mt-2 text-lg font-bold">
                  {billing ? `${cycleLabel[billing.billingCycle] ?? billing.billingCycle} - ${formatPrice(billing.subscriptionPrice ?? 0)}` : '-'}
                </p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Fim do trial</p>
                <p className="mt-2 text-lg font-bold">
                  {billing?.trialEndsAt ? new Date(billing.trialEndsAt).toLocaleDateString('pt-BR') : 'Nao definido'}
                </p>
              </div>
              <div className="rounded-2xl border border-[#2A2A2A] bg-[#151515] p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Proxima cobranca</p>
                <p className="mt-2 text-lg font-bold">
                  {billing?.nextBillingAt ? new Date(billing.nextBillingAt).toLocaleDateString('pt-BR') : 'Apos pagamento'}
                </p>
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <button
              type="button"
              onClick={openCheckout}
              disabled={redirecting || !user?.establishmentId}
              className="btn-primary flex flex-1 items-center justify-center gap-2"
            >
              {redirecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />}
              Pagar assinatura
            </button>
            <button
              type="button"
              onClick={openPortal}
              disabled={redirecting || !user?.establishmentId}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-[#2A2A2A] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#1A1A1A]"
            >
              <ShieldCheck className="h-4 w-4" />
              Portal do cliente
            </button>
          </div>
        </div>
      </section>
    </main>
  );
};

export default AdminBillingRequired;
